import {
  Account_Create,
  Account_Promote,
  AccountType,
  deleteV1CompaniesCompanyIdAccountsId,
  getV1CompaniesAccountsTypeahead,
  getV1CompaniesCompanyIdAccounts,
  getV1CompaniesCompanyIdAccountsId,
  patchV1CompaniesCompanyIdAccountsId,
  postV1CompaniesCompanyIdAccounts,
  postV1CompaniesCompanyIdAccountsInvite,
  putV1CompaniesCompanyIdAccountsIdPromote,
  RateOwnerType,
} from '@treadinc/horizon-api-spec';
import type {
  GetV1CompaniesAccountsTypeaheadData,
  GetV1CompaniesCompanyIdAccountsData,
} from '@treadinc/horizon-api-spec/types/types.gen';
import axios, { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { AccountTypesFilter } from '~constants/enums';
import useCancelToken from '~hooks/useCancelToken/useCancelToken';
import { CompanyBasic } from '~hooks/useCompany/models';
import { Filters } from '~pages/Settings/Accounts/useAccountFilters';
import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import { useStores } from '~store';
import isEnumValue from '~utils/isEnumValue';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { Account, AccountTypeahead } from './models';

interface PaginateOnScrollParams {
  cursor?: string;
  query?: string;
}

interface CreateAccountParams {
  account: Account;
}

interface CreateCustomerAccountByNameParams {
  companyId: string;
  name: string;
}

interface ConnectedAccountFormAccount extends Record<string, unknown> {
  autoAcceptOrders?: boolean | { value: boolean };
  company?: CompanyBasic;
  inviteTypes: string[];
  primaryContact: {
    firstName: string;
    lastName: string;
    [key: string]: unknown;
  };
}

interface CreateConnectedAccountParams {
  account: ConnectedAccountFormAccount;
}

interface CreateConnectedAccountByCompanyIdParams {
  account: ConnectedAccountFormAccount & { company: CompanyBasic };
}

interface DeleteAccountParams {
  companyId?: string;
  id: string;
}

interface PromoteAccountParams {
  body: Account_Promote;
  companyId: string;
  id: string;
}

interface AccountContactFormValue extends Record<string, unknown> {
  email?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  phone?: string;
}

interface UpdateAccountFormValue extends Record<string, unknown> {
  autoAcceptOrders?: boolean | { value: boolean };
  billingContact: AccountContactFormValue;
  connectedCompany?: unknown;
  id: string;
  primaryContact: AccountContactFormValue;
}

interface UpdateAccountParams {
  account: UpdateAccountFormValue;
  companyId?: string;
}

interface GetAccountsParams {
  accountTypes?: AccountTypesFilter[] | RateOwnerType[];
  companyId: string;
  cursor?: string;
  filters?: Filters;
  limit?: number;
  link?: PaginationLink;
  searchQuery?: string;
  signal?: AbortSignal;
}

interface GetAccountsByCompanyIdParams {
  companyId: string;
  cursor?: string;
  link?: PaginationLink;
  searchQuery?: string;
  signal?: AbortSignal;
}

interface GetAccountsByCompanyIdPaginatedParams extends GetAccountsByCompanyIdParams {
  limit: number;
}

interface GetAccountsTypeaheadProps extends PaginateOnScrollParams {
  accountTypes?: AccountType[];
  companyId: string;
  searchParams?: {
    accountTypes?: AccountType[];
    isConnected?: boolean;
    limit?: number;
    link?: {
      cursor: string;
      type: PaginationLink;
    };
    query?: string;
  };
}

const getAutoAcceptOrdersValue = (
  autoAcceptOrders: boolean | { value: boolean } | undefined,
): boolean => {
  if (autoAcceptOrders && typeof autoAcceptOrders === 'object') {
    return autoAcceptOrders.value;
  }

  return autoAcceptOrders ?? false;
};

export default function useAccount() {
  const { userStore } = useStores();
  const effectiveCompanyId = getEffectiveUserCompanyId(userStore);

  const updateAccount = async ({ account, companyId }: UpdateAccountParams) => {
    const connectedPrimaryContactName =
      `${account.primaryContact.firstName || ''} ${account.primaryContact.lastName || ''}`.trim();
    const connectedBillingContactName =
      `${account.billingContact.firstName || ''} ${account.billingContact.lastName || ''}`.trim();
    const isConnectedAccount = Boolean(account.connectedCompany);

    const primaryContactName = isConnectedAccount
      ? connectedPrimaryContactName
      : account.primaryContact.name;
    const billingContactName = isConnectedAccount
      ? connectedBillingContactName
      : account.billingContact.name;

    const primaryContact = {
      ...account.primaryContact,
      name: primaryContactName || ' ',
      email: account.primaryContact.email || ' ',
      phone: account.primaryContact.phone || ' ',
    };
    const billingContact = {
      ...account.billingContact,
      name: billingContactName || ' ',
      email: account.billingContact.email || ' ',
      phone: account.billingContact.phone || ' ',
    };

    const updatedAccount = {
      ...account,
      autoAcceptOrders: getAutoAcceptOrdersValue(account.autoAcceptOrders),
      billingContact,
      primaryContact,
    };

    try {
      const response = await patchV1CompaniesCompanyIdAccountsId({
        path: {
          'company-id':
            companyId ||
            (account.company as CompanyBasic | undefined)?.id ||
            effectiveCompanyId,
          id: account.id,
        },
        body: Account.deparseUpdate(updatedAccount as Account),
      });
      const parsedAccount = Account.parse(response.data.data);

      return parsedAccount;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        throw error;
      }

      connection.handleRequestError(error, t('error_messages.account.failed_to_update'));
      throw error;
    }
  };

  const getAccountsByCompanyId = async ({
    companyId,
    cursor,
    link,
    searchQuery,
    signal,
  }: GetAccountsByCompanyIdParams) => {
    try {
      const response = await getV1CompaniesCompanyIdAccounts({
        path: { 'company-id': companyId },
        query: {
          ...(searchQuery ? { 'search[datagrid]': searchQuery } : {}),
          ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
        },
        signal,
      });

      return {
        data: response.data.data.map(Account.parse),
        pagination: extractPagination(response),
      };
    } catch (error) {
      if (signal?.aborted) {
        throw error;
      }

      connection.handleRequestError(
        error,
        t('error_messages.account.failed_to_fetch_company_accounts'),
      );
      throw error;
    }
  };

  const getAccountsByCompanyIdPaginated = async ({
    companyId,
    cursor,
    limit,
    link,
    searchQuery,
    signal,
  }: GetAccountsByCompanyIdPaginatedParams) => {
    try {
      const response = await getV1CompaniesCompanyIdAccounts({
        path: { 'company-id': companyId },
        query: {
          'page[limit]': limit,
          ...(link && cursor ? { [`page[${link}]`]: cursor } : {}),
          ...(searchQuery ? { 'search[datagrid]': searchQuery } : {}),
        },
        signal,
      });

      return {
        data: response.data.data.map(Account.parse),
        pagination: extractPagination(response),
      };
    } catch (error) {
      if (signal?.aborted) {
        throw error;
      }

      connection.handleRequestError(
        error,
        t('error_messages.account.failed_to_fetch_company_accounts'),
      );
      throw error;
    }
  };

  const getAccountsByCompanyIdTypeaheadCancelToken = useCancelToken();

  const getAccountsByCompanyIdTypeahead = async ({
    accountTypes,
    companyId,
    cursor,
    query,
    searchParams = {
      limit: 50,
      isConnected: false,
    },
  }: GetAccountsTypeaheadProps) => {
    const searchLimit = searchParams.limit ?? 30;

    const params: GetV1CompaniesAccountsTypeaheadData['query'] = {
      'page[limit]': searchLimit,
    };

    if (searchParams.query || query) {
      params['search[query]'] = searchParams.query || query;
    }

    if (searchParams.accountTypes) {
      params['filter[account_types]'] = searchParams.accountTypes;
    }

    if (searchParams.isConnected) {
      params['filter[connected]'] = searchParams.isConnected;
    }

    if (accountTypes) {
      params['filter[account_types]'] = accountTypes;
    }

    if (cursor) {
      params['page[after]'] = cursor;
    } else if (searchParams.link?.type && searchParams.link.cursor) {
      params[`page[${searchParams.link.type}]`] = searchParams.link.cursor;
    }

    try {
      const cancelToken = getAccountsByCompanyIdTypeaheadCancelToken.issueToken();
      const response = await getV1CompaniesAccountsTypeahead({
        path: { 'company-id': companyId },
        query: { ...params },
        cancelToken,
      });
      const accounts = response.data.data.map((account) =>
        AccountTypeahead.parse(account),
      );
      const pagination = extractPagination(response);

      return { data: accounts, pagination };
    } catch (error) {
      if (axios.isCancel(error)) {
        return {
          data: [],
          pagination: { limit: searchLimit, page: 1 },
        };
      }

      connection.handleRequestError(
        error,
        t('error_messages.account.failed_to_fetch_company_accounts'),
      );
      throw error;
    } finally {
      getAccountsByCompanyIdTypeaheadCancelToken.discardToken();
    }
  };

  const getAllCompanyAccounts = useCallback(
    async ({
      accountTypes,
      companyId,
      cursor,
      filters,
      limit = MAX_API_PAGE_SIZE,
      link,
      searchQuery,
      signal,
    }: GetAccountsParams) => {
      const query: GetV1CompaniesCompanyIdAccountsData['query'] = {
        'page[limit]': limit,
      };

      if (searchQuery) {
        query['search[datagrid]'] = searchQuery;
      }

      if (link && cursor) {
        query[`page[${link}]`] = cursor;
      }

      // `all` and `driver` aren't account types the API filters on
      const filterableAccountTypes = accountTypes?.filter((accountType) =>
        isEnumValue(AccountType, accountType),
      );

      if (filterableAccountTypes?.length) {
        query['filter[account_types]'] = filterableAccountTypes;
      }

      if (filters?.departments?.length) {
        query['filter[department_ids]'] = filters.departments;
      }

      if (filters?.connected_company?.company_types?.length) {
        query['filter[connected_company][company_types]'] =
          filters.connected_company.company_types;
      }

      if (filters?.labels?.length) {
        query['filter[label_ids]'] = filters.labels;
      }

      const response = await getV1CompaniesCompanyIdAccounts({
        path: { 'company-id': companyId },
        query,
        signal,
      });

      return {
        data: response.data.data.map(Account.parse),
        pagination: extractPagination(response),
      };
    },
    [],
  );

  const getAccountByIdString = async (id: string, companyId?: string) => {
    try {
      const response = await getV1CompaniesCompanyIdAccountsId({
        path: { 'company-id': companyId || effectiveCompanyId, id },
      });

      return Account.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.account.failed_to_fetch_selected'),
      );
      throw error;
    }
  };

  const createAccount = async ({ account }: CreateAccountParams) => {
    try {
      const parsedAccount = {
        ...account,
        autoAcceptOrders: getAutoAcceptOrdersValue(account.autoAcceptOrders),
      };

      const response = await postV1CompaniesCompanyIdAccounts({
        path: {
          'company-id': account.company?.id || effectiveCompanyId,
        },
        body: Account.deparse(parsedAccount as Account),
      });
      const accountSaved = Account.parse(response.data.data);

      return accountSaved;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        throw error;
      }

      connection.handleRequestError(error, t('error_messages.account.failed_to_create'));
      throw error;
    }
  };

  // Name-only CUSTOMER create backing the "Create account" chip in form
  // typeaheads. The body is typed Account_Create directly so callers don't
  // have to fake an Account instance; the explicit false/[] fields match the
  // payload the legacy project/order forms sent for the same flow.
  const createCustomerAccountByName = useCallback(
    async ({ companyId, name }: CreateCustomerAccountByNameParams) => {
      try {
        const body: Account_Create = {
          name,
          account_types: [AccountType.CUSTOMER],
          auto_accept_orders: false,
          jobs_splittable: false,
          equipment_options_restricted: false,
          label_ids: [],
        };
        const response = await postV1CompaniesCompanyIdAccounts({
          path: { 'company-id': companyId || effectiveCompanyId },
          body,
        });
        const accountSaved = Account.parse(response.data.data);

        return accountSaved;
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.account.failed_to_create'),
        );
        throw error;
      }
    },
    [effectiveCompanyId],
  );

  const createConnectedAccount = async ({ account }: CreateConnectedAccountParams) => {
    const fullName = `${account.primaryContact.firstName} ${account.primaryContact.lastName}`;
    const invitesToSend = {
      email: account.inviteTypes.includes('email'),
      sms: account.inviteTypes.includes('sms'),
    };
    const updatedAccount = {
      ...account,
      primaryContact: {
        ...account.primaryContact,
        name: fullName,
      },
      invitesToSend,
      autoAcceptOrders: getAutoAcceptOrdersValue(account.autoAcceptOrders),
    };

    try {
      const response = await postV1CompaniesCompanyIdAccountsInvite({
        path: { 'company-id': effectiveCompanyId },
        body: Account.deparseConnectedAccount(updatedAccount),
      });
      const accountSaved = Account.parse(response.data.data);

      return accountSaved;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        throw error;
      }

      connection.handleRequestError(
        error,
        t('error_messages.account.failed_to_create_connected'),
      );
      throw error;
    }
  };

  const createConnectedAccountByCompanyId = async ({
    account,
  }: CreateConnectedAccountByCompanyIdParams) => {
    const fullName = `${account.primaryContact.firstName} ${account.primaryContact.lastName}`;
    const invitesToSend = {
      email: account.inviteTypes.includes('email'),
      sms: account.inviteTypes.includes('sms'),
    };
    const companyId = account.company.id;
    const updatedAccount = {
      ...account,
      primaryContact: {
        ...account.primaryContact,
        name: fullName,
      },
      invitesToSend,
      autoAcceptOrders: getAutoAcceptOrdersValue(account.autoAcceptOrders),
    };

    try {
      const response = await postV1CompaniesCompanyIdAccountsInvite({
        path: { 'company-id': companyId },
        body: Account.deparseConnectedAccount(updatedAccount),
      });
      const accountSaved = Account.parse(response.data.data);

      return accountSaved;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        throw error;
      }

      connection.handleRequestError(
        error,
        t('error_messages.account.failed_to_create_connected'),
      );
      throw error;
    }
  };

  const deleteAccount = async ({ companyId, id }: DeleteAccountParams) => {
    try {
      await deleteV1CompaniesCompanyIdAccountsId({
        path: { 'company-id': companyId || effectiveCompanyId, id },
      });
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        throw error;
      }

      connection.handleRequestError(error, t('error_messages.account.failed_to_delete'));
      throw error;
    }
  };

  const promoteAccount = async ({ body, companyId, id }: PromoteAccountParams) => {
    try {
      const response = await putV1CompaniesCompanyIdAccountsIdPromote({
        path: { 'company-id': companyId, id },
        body,
      });
      const parsedAccount = Account.parse(response.data.data);

      return parsedAccount;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        throw error;
      }

      connection.handleRequestError(error, t('error_messages.account.failed_to_promote'));
      throw error;
    }
  };

  return {
    createAccount,
    createConnectedAccount,
    createConnectedAccountByCompanyId,
    createCustomerAccountByName,
    deleteAccount,
    getAccountByIdString,
    getAccountsByCompanyId,
    getAccountsByCompanyIdPaginated,
    getAccountsByCompanyIdTypeahead,
    getAllCompanyAccounts,
    promoteAccount,
    updateAccount,
  } as const;
}
